<template>
  <section>
    <b-button
      :to="'surveys/create'"
      variant="primary"
      class="mb-2"
      size="sm"
    >
      <GjIcon
        class="mr-50"
        name="Plus"
      />
      {{ $tk('ClientPortal.Survey.CreateSurvey') }}
    </b-button>
    <b-card
      class="mb-0 client-table--survey"
      no-body
    >
      <template v-if="loadingSurveys">
        <b-skeleton-table
          :rows="10"
          :columns="fields.length"
        />
      </template>
      <template v-else>
        <b-table
          :items="items"
          :fields="fields"
          class="client-table__data survey-table"
          show-empty
          :empty-text="$tk('ClientPortal.Survey.NoSurveysHaveBeenCreatedYet')"
          responsive
          :tbody-tr-class="newRecordRowStyle"
        >
          <template #empty="scope">
            <div class="text-center p-3 client-table__empty">
              <div class="empty__client-table">
                <GjIcon
                  size="64px"
                  name="Question"
                />
                <div class="mb-50">
                  {{ scope.emptyText }}
                </div>
                <router-link :to="'surveys/create'">
                  <GjIcon
                    size="14"
                    name="Plus"
                  />
                  <span class="create-text"> {{ $tk('ClientPortal.Survey.CreateSurvey') }}</span>
                </router-link>
              </div>
            </div>
          </template>
          <template #cell(name)="data">
            <b-link
              :to="'surveys/details/' + data.item.id"
            >
              {{ data.item.name }}
            </b-link>
          </template>
          <template #cell(ads)="data">
            <div v-if="data.item.totalAdsCount > 0">
              {{ data.item.totalAdsRunning }}/{{ data.item.totalAdsCount }} {{ $tk('ClientPortal.Survey.AdsRunning') }}
            </div>
            <div v-else>
              -
            </div>
          </template>
          <template #cell(details)="data">
            <div class="d-flex align-items-center justify-content-between">
              <span>
                <div
                  v-if="data.item.insertedByUser != null"
                  class="text-muted"
                >
                  <span class="pr-50">{{ $tk('ClientPortal.Survey.CreatedBy') }}</span>
                  <b-avatar
                    v-b-tooltip.hover.top="`${data.item.insertedByUser.displayName}`"
                    variant="transparent"
                    class="avatar"
                    size="20px"
                    :src="data.item.insertedByUser.avatar"
                  />
                  {{ `${$tk('ClientPortal.Survey.At')} ${getFormattedDate(data.item.insertedByUser.dateTime)}` }}
                </div>
                <div
                  v-if="data.item.updatedByUser != null"
                  class="text-muted"
                >
                  {{ $tk('ClientPortal.Survey.UpdatedBy') }}
                  <b-avatar
                    v-b-tooltip.hover.top="`${data.item.updatedByUser.displayName}`"
                    variant="transparent"
                    class="avatar"
                    size="20px"
                    :src="data.item.updatedByUser.avatar"
                  />
                  {{ `${$tk('ClientPortal.Survey.At')} ${getFormattedDate(data.item.updatedByUser.dateTime)}` }}
                </div>
              </span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
                button-class="p-0"
                right
              >
                <template v-slot:button-content>
                  <GjIcon name="Moreoptions_fill" />
                </template>
                <b-dropdown-item :to="'surveys/edit/' + data.item.id">
                  <GjIcon
                    class="mr-50"
                    name="Edit"
                  />
                  {{ $tk('ClientPortal.Survey.Edit') }}
                </b-dropdown-item>
                <b-dropdown-item @click="showDuplicateDialog(data.item.id)">
                  <GjIcon
                    class="mr-50"
                    name="Copy"
                  />
                  {{ $tk('ClientPortal.Survey.Duplicate') }}
                </b-dropdown-item>
                <b-dropdown-item @click="showDeleteDialog(data.item.id)">
                  <GjIcon
                    class="mr-50"
                    name="Delete"
                  />
                  {{ $tk('ClientPortal.Survey.Delete') }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>
      </template>
      <b-card-footer
        v-if="items.length > 0 || !loadingSurveys"
        class="client-table__footer d-flex"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
          class="my-0 mr-1"
          hide-goto-end-buttons
        >
          <template #prev-text>
            <GjIcon
              class="mr-25"
              name="ArrowLeft"
            />
          </template>
          <template #next-text>
            <GjIcon
              class="ml-25"
              name="ArrowRight"
            />
          </template>
        </b-pagination>
        <span>
          {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }}
          {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }}
          {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }}
          {{ $tk('ClientPortal.Pagination.Items') }}
        </span>
      </b-card-footer>
    </b-card>
    <b-modal
      ref="duplicateSurveyModal"
      :title="$tk('ClientPortal.Audience.DuplicateHeaderMessage')"
      centered
    >
      {{ $tk('ClientPortal.Audience.AreYouSureYouWantToDuplicateThisSurvey') }}
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="success"
          :disabled="surveyDialog.loading"
          size="sm"
          @click="confirmDuplicate"
        >
          {{ $tk('ClientPortal.Form.Actions.Duplicate') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal
      ref="deleteSurveyModal"
      :title="$tk('ClientPortal.Survey.DeleteHeaderMessage')"
      centered
    >
      {{ $tk('ClientPortal.Survey.AreYouSureYouWantToDeleteThisSurvey') }}
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="surveyDialog.loading"
          size="sm"
          @click="confirmDelete"
        >
          {{ $tk('ClientPortal.Form.Actions.Delete') }}
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import SurveysService from '@/api/client/surveys.service'
import notification from '@/mixins/toastification'
import { computed, ref } from '@vue/composition-api'
import store from '@/store'

export default {
  components: {
    GjIcon,
  },
  mixins: [notification],
  data() {
    return {
      items: [],
      currentPage: 1,
      perPage: 10,
      loadingSurveys: false,
      totalRowsNumber: 0,
      surveyDialog: {
        loading: false,
        surveyId: 0,
      },
    }
  },
  computed: {
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    fields() {
      return [
        {
          key: 'name',
          label: this.$tk('ClientPortal.Survey.Name'),
        },
        {
          key: 'description',
          label: this.$tk('ClientPortal.Survey.Description'),
        },
        {
          key: 'surveyQuestionsCount',
          label: this.$tk('ClientPortal.Survey.QuestionsCount'),
        },
        {
          key: 'surveyPrivacyTypeText',
          label: this.$tk('ClientPortal.Survey.PrivacyType'),
        },
        {
          key: 'surveyResponsesCount',
          label: this.$tk('ClientPortal.Survey.ResponsesCount'),
        },
        {
          key: 'ads',
          label: this.$tk('ClientPortal.Survey.Ads'),
        },
        {
          key: 'details',
          label: this.$tk('ClientPortal.Survey.Details'),
          tdClass: 'w-25',
        },
      ]
    },
    totalRows() {
      return this.totalRowsNumber
    },
  },
  watch: {
    '$route.params.accountId': 'reloadSurveys',
    currentPage() {
      this.reloadSurveys()
    },
  },
  async mounted() {
    this.reloadSurveys()
  },
  methods: {
    async reloadSurveys() {
      try {
        this.loadingSurveys = true
        const surveys = await SurveysService.getSurveys(this.$route.params.accountId, ((this.currentPage - 1) * 10), 10)
        this.items = surveys.items
        this.totalRowsNumber = surveys.total
      } catch {
        this.error(this.$tk('ClientPortal.Survey.Error.ErrorGettingSurveys'))
      } finally {
        this.loadingSurveys = false
      }
    },
    showDuplicateDialog(surveyId) {
      this.surveyDialog.surveyId = surveyId
      this.$refs.duplicateSurveyModal.show()
    },
    showDeleteDialog(surveyId) {
      this.surveyDialog.surveyId = surveyId
      this.$refs.deleteSurveyModal.show()
    },
    async confirmDuplicate() {
      this.surveyDialog.loading = true
      try {
        const response = await SurveysService.duplicateSurvey(this.surveyDialog.surveyId, this.$route.params.accountId)
        if (response.saved) {
          const el = Array.from(document.querySelectorAll('.survey-table > table > tbody > tr:first-child')).pop()
          el.classList.remove('table-row-animation')
          // eslint-disable-next-line no-unused-expressions
          el.offsetHeight // trigger reflow
          el.classList.add('table-row-animation')
          this.items.unshift(response.survey)
          this.success(this.$tk('ClientPortal.Survey.DuplicatedSurveySuccessfully'))
        } else {
          this.error(this.$tk('ClientPortal.Survey.Error.ErrorWhileDuplicatingSurvey'))
        }
      } catch {
        this.error(this.$tk('ClientPortal.Survey.Error.ErrorWhileDuplicatingSurvey'))
      } finally {
        this.surveyDialog.loading = false
        this.$refs.duplicateSurveyModal.hide()
      }
    },
    async confirmDelete() {
      this.surveyDialog.loading = true
      try {
        const response = await SurveysService.deleteSurvey(this.surveyDialog.surveyId, this.$route.params.accountId)
        if (response.saved) {
          this.items = this.items.filter(x => x.id !== this.surveyDialog.surveyId)
          this.success(response.message)
        } else { this.error(response.message) }
      } catch {
        this.error(this.$tk('ClientPortal.Survey.Error.ErrorWhileDeletingSurvey'))
      } finally {
        this.surveyDialog.loading = false
        this.$refs.deleteSurveyModal.hide()
      }
    },
  },
  setup() {
    const isCreatedNew = computed(() => store.state.app.isCreatedNew)
    const alreadySet = ref(false)
    const firstRow = ref(null)
    const removeAnimation = () => {
      if (isCreatedNew.value) {
        if (firstRow.value) {
          setTimeout(() => {
            store.commit('app/TOGGLE_NEW_CREATE_STATE', false)
          }, 3000)
        }
      }
    }
    const newRecordRowStyle = () => {
      if (!alreadySet.value) {
        firstRow.value = Array.from(document.querySelectorAll('.survey-table > table > tbody > tr:first-child')).pop()
        if (firstRow.value) {
          if (isCreatedNew.value) {
            firstRow.value.classList.add('table-row-animation')
          } else if (firstRow.value.classList.contains('table-row-animation')) {
            firstRow.value.classList.remove('table-row-animation')
          }
          alreadySet.value = true
          removeAnimation()
        }
      }
      return ''
    }

    const getFormattedDate = date => date.slice(0, date.length - 5)

    return {
      isCreatedNew,
      newRecordRowStyle,
      getFormattedDate,
    }
  },
}
</script>
<style lang="scss">
@import '@/assets/scss/views/added_row_animation.scss';
.avatar{
  background-color: #fff;
  &:hover {
    transform: scale(1.2);
    z-index: 5
  }
}
</style>
